import * as React from 'react';
import escapeRegExp from 'lodash/escapeRegExp';
import { st, classes } from './Highlighter.st.css';
import { HighlightedItemProps } from './Highlighter.types';

interface MatchBoundaries {
  first: number;
  last: number;
}

class HighlightedItem extends React.PureComponent<HighlightedItemProps> {
  renderElement(): React.ReactNode {
    const { children, match } = this.props;

    if (match && typeof children === 'string') {
      const matchRegExp = this.getMatchRegExp();

      return this.highlightChildren(children, matchRegExp);
    }

    return children;
  }

  getMatchRegExp(): RegExp {
    const { match, caseSensitive } = this.props;
    return new RegExp(escapeRegExp(match), caseSensitive ? '' : 'i');
  }

  getMatchBoundaries(
    subject: string,
    matchRegExp: RegExp,
  ): MatchBoundaries | undefined {
    const matches = matchRegExp.exec(subject);

    if (!matches) {
      return undefined;
    }

    return {
      first: matches.index,
      last: matches.index + matches[0].length,
    };
  }

  getMatchReactKey(index: number): string {
    return `match-index-${index}`;
  }

  highlightChildren(children: string, matchRegExp: RegExp): React.ReactNode[] {
    const processedChildren: React.ReactNode[] = [];
    let matchIndex = 0;

    while (children) {
      if (!matchRegExp.test(children)) {
        processedChildren.push(
          this.renderPlain(children, this.getMatchReactKey(matchIndex++)),
        );
        return processedChildren;
      }

      const boundaries = this.getMatchBoundaries(children, matchRegExp);

      if (!boundaries) {
        break;
      }

      const nonMatch = children.slice(0, boundaries.first);

      if (nonMatch) {
        processedChildren.push(
          this.renderPlain(nonMatch, this.getMatchReactKey(matchIndex++)),
        );
      }

      const match = children.slice(boundaries.first, boundaries.last);

      if (match) {
        processedChildren.push(
          this.renderHighlight(match, this.getMatchReactKey(matchIndex++)),
        );
      }

      children = children.slice(boundaries.last);
    }

    return processedChildren;
  }

  renderPlain(plainString: string, key: string): React.ReactElement {
    return <span key={key}>{plainString}</span>;
  }

  renderHighlight(matchString: string, key: string): React.ReactElement {
    const { emphasize = 'text' } = this.props;
    if (emphasize === 'background') {
      return (
        <span
          key={key}
          className={st(classes.root, {
            emphasize,
          })}
        >
          {matchString}
        </span>
      );
    }

    return <strong key={key}>{matchString}</strong>;
  }

  render() {
    const { dataHook } = this.props;
    return <span data-hook={dataHook}>{this.renderElement()}</span>;
  }
}

export { HighlightedItem };
